import { useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { FINANCE_EXPORT_REPORTS, IFinanceExportReport } from '@entities/financeExportReport';
import { financeExportRequestsCheckStatusFunc } from '@entities/financeExportReport/api/api-funcs';
import { keysToCamel } from '@shared/helpers';

import { useGetFinanceExportReports } from './useGetFinanceExportReports';

const PENDING_STATUSES: IFinanceExportReport['status'][] = ['new', 'in_progress'];

const CHECK_STATUS_QUERY_KEY = [...FINANCE_EXPORT_REPORTS, 'checkStatus'] as const;

const getPendingIds = (reports: IFinanceExportReport[] | undefined): number[] =>
    reports?.filter((r) => PENDING_STATUSES.includes(r.status)).map((r) => r.id) ?? [];

export const useCheckFinanceExportReportsStatus = () => {
    const queryClient = useQueryClient();

    const { data: reports } = useGetFinanceExportReports();

    const pendingIds = useMemo(() => getPendingIds(reports), [reports]);
    const hasPending = pendingIds.length > 0;

    const { data: checkResult } = useQuery({
        queryKey: CHECK_STATUS_QUERY_KEY,
        queryFn: () => financeExportRequestsCheckStatusFunc(pendingIds),
        select: (data) => keysToCamel<IFinanceExportReport[]>(data?.data?.data),
        enabled: hasPending,
        refetchInterval: hasPending ? 5000 : false,
        staleTime: 0,
        gcTime: 0,
    });

    useEffect(() => {
        if (!checkResult?.length) return;

        const hasStatusChange = checkResult.some((updated) => {
            const current = reports?.find((r) => r.id === updated.id);
            return current && current.status !== updated.status;
        });

        if (hasStatusChange) {
            queryClient.invalidateQueries({ queryKey: FINANCE_EXPORT_REPORTS });
        }
    }, [checkResult, reports, queryClient]);
};
